/**
 * Shared constants, helpers, and small components for Result.js and PreviousResult.js
 */
import React from "react";
import { Box, Typography } from "@mui/material";
import Badge from "@mui/material/Badge";
import { styled } from "@mui/material/styles";
import DeleteUserForm from "./DeleteUserForm.json";

// Lottie default options
export const defaultOptions = {
  loop: true,
  autoplay: true,
  animationData: DeleteUserForm,
  rendererSettings: {
    preserveAspectRatio: "xMidYMid slice",
  },
};

export const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    right: 15,
    top: 10,
    border: `2px solid ${theme.palette.background.paper}`,
    padding: "0 4px",
    background: "#2BB344",
  },
}));

const imageExists = (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
};

export const getIconToolName = async (skillName) => {
  const svgIconPath = `${process.env.PUBLIC_URL}/assets/images/icons/${skillName}.svg`;
  const pngIconPath = `${process.env.PUBLIC_URL}/assets/images/icons/${skillName}.png`;
  const darkIconPath = `${process.env.PUBLIC_URL}/assets/images/dark-icons/${skillName}.svg`;

  if (await imageExists(svgIconPath)) {
    return svgIconPath;
  } else if (await imageExists(pngIconPath)) {
    return pngIconPath;
  } else if (await imageExists(darkIconPath)) {
    return darkIconPath;
  }
  return null;
};

export const salaryData = [
  { id: 0, iconSrc: "/assets/images/unchecked-1-3lac.png", checkedSrc: "/assets/images/1-3lac.png", label: "1-3 Lacs", value: "-30%" },
  { id: 1, iconSrc: "/assets/images/unchecked-3-5lac.png", checkedSrc: "/assets/images/3-5lac.png", label: "3-5 Lacs", value: "-20%" },
  { id: 2, iconSrc: "/assets/images/unchecked-5-10lac.png", checkedSrc: "/assets/images/5-10lac.png", label: "5-10 Lacs", value: "-10%" },
  { id: 3, iconSrc: "/assets/images/unchecked-10-15lac.png", checkedSrc: "/assets/images/10-15lac.png", label: "10-15 Lacs", value: "-5%" },
  { id: 4, iconSrc: "/assets/images/unchecked-15-20lac.png", checkedSrc: "/assets/images/15-20lac.png", label: "15-20 Lacs", value: "10%" },
  { id: 5, iconSrc: "/assets/images/unchecked-20-25lac.png", checkedSrc: "/assets/images/20-25lac.png", label: "20-25 Lacs", value: "20%" },
  { id: 6, iconSrc: "/assets/images/unchecked-25-30lac.png", checkedSrc: "/assets/images/25-30lac.png", label: "25-30 Lacs", value: "30%" },
];

export function filterSalaryPartitions(value, index, array) {
  const isNotFirstOrLast = index > 0 && index < array.length - 1;
  return isNotFirstOrLast ? `${value}` : index === 0 ? `<${value}` : `>${value}`;
}

export const formatCurrencyString = (str) => {
  const symbolFirst = /^[^\d]+/;
  const symbolLast = /[^\d]+$/;
  if (symbolFirst.test(str)) return str;
  if (symbolLast.test(str)) {
    const parts = str.split(symbolLast);
    const number = parts[0].trim();
    const symbol = str.match(symbolLast)[0].trim();
    return `${symbol}${number}`;
  }
  return str;
};

export function GetBackGroundColor(skillDemand, type) {
  if (skillDemand === "Trending") {
    switch (type) {
      case "y": return "#278C37";
      case "tech": return "#278C37";
      case "mgmt": return "#30B044";
      case "management": return "#30B044";
      case "generic": return "#38C94E";
      default: return "#278C37";
    }
  }
  if (skillDemand === "Obsolete") {
    switch (type) {
      case "y": return "#028EDF";
      case "tech": return "#028EDF";
      case "mgmt": return "#09A2FA";
      case "management": return "#09A2FA";
      case "generic": return "#79CDFD";
      default: return "#028EDF";
    }
  }
  if (skillDemand === "In demand") {
    switch (type) {
      case "y": return "#E9B112";
      case "tech": return "#E9B112";
      case "mgmt": return "#FEC113";
      case "management": return "#FEC113";
      case "generic": return "#FAD15D";
      default: return "#E9B112";
    }
  }
  if (skillDemand === "Getting outdated fast") {
    switch (type) {
      case "y": return "#C60015";
      case "tech": return "#C60015";
      case "mgmt": return "#E2031B";
      case "management": return "#E2031B";
      case "generic": return "#FF465A";
      default: return "#C60015";
    }
  }
}

export function checkYearWithGpt(option) {
  const year = option?.expiryYear;
  const currentYear = new Date().getFullYear();
  const minYearDiff = 5;
  const minYearForOutdatedSkill = 4;
  const minYearForCurrentSkill = 1;

  if (!year) {
    return { label: "Default", barPrimaryColor: "#e0e0e0", barSecondaryColor: "#e0e0e0" };
  }

  if (year > currentYear) {
    if (option?.chat_gpt === false && option?.estYear === 2010) {
      return {
        label: "Obsolete",
        barPrimaryColor: "#028EDF",
        barSecondaryColor: GetBackGroundColor("Obsolete", option?.type),
        color: "#ffffff",
      };
    }
    return {
      label: "Trending",
      barPrimaryColor: "#278C37",
      barSecondaryColor: GetBackGroundColor("Trending", option?.type),
      color: "#ffffff",
    };
  }
  if (year >= currentYear - minYearForCurrentSkill) {
    return {
      label: "In demand",
      barPrimaryColor: "#E9B112",
      barSecondaryColor: GetBackGroundColor("In demand", option?.type),
      color: "#ffffff",
    };
  }
  if (year >= currentYear - minYearForOutdatedSkill) {
    return {
      label: "Getting outdated fast",
      barPrimaryColor: "#C60015",
      barSecondaryColor: GetBackGroundColor("Getting outdated fast", option?.type),
      color: "#ffffff",
    };
  }
  if (year <= currentYear - minYearDiff) {
    return {
      label: "Obsolete ",
      barPrimaryColor: "#028EDF",
      barSecondaryColor: GetBackGroundColor("Obsolete", option?.type),
      color: "#ffffff",
    };
  }
}

// Salary feedback radio icons (shared UI)
export const RadioIcon = ({ src }) => (
  <Box
    sx={{
      width: { xs: "60px", md: "80px", lg: "100px" },
      height: { xs: "60px", md: "80px", lg: "100px" },
      borderRadius: "50%",
      overflow: "hidden",
      "& >img": { width: "100%", height: "100%" },
    }}
  >
    <img src={src} alt="radio-img" loading="lazy" />
  </Box>
);

export const CheckedRadioIcon = ({ src }) => (
  <Box
    sx={{
      width: { xs: "60px", md: "80px", lg: "100px" },
      height: { xs: "60px", md: "80px", lg: "100px" },
      borderRadius: "50%",
      overflow: "hidden",
      "& >img": { width: "100%", height: "100%" },
    }}
  >
    <img src={src} alt="checked-radio-img" loading="lazy" />
  </Box>
);

/**
 * Common section title for Result and PreviousResult pages.
 * @param {React.ReactNode} icon - MUI icon component (e.g. <WorkIcon sx={{...}} />)
 * @param {React.ReactNode} children - Title text
 */
export function ResultSectionTitle({ icon, children }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        mb: "32px",
        gap: { xs: 1.5, md: 2 },
        width: "100%",
      }}
    >
      <Box
        sx={{
          width: { xs: "40px", sm: "40px", md: "50px" },
          height: { xs: "40px", sm: "40px", md: "50px" },
          backgroundColor: "#2bb344",
          color: "#ffffff",
          borderRadius: "30%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 600,
          pl: { xs: 1, md: 2 },
          fontSize: { xs: "20px", md: "32px" },
          textAlign: "center",
          lineHeight: 1.2,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}
